import React, { FC, memo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Typography
} from '@material-ui/core';
import PhoneIcon from '@material-ui/icons/Phone';
import RoomIcon from '@material-ui/icons/Room';
import LinkIcon from '@material-ui/icons/Link';
import ScheduleIcon from '@material-ui/icons/Schedule';
import { colors } from 'src/constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CrmTicket {
  id?: string | number;
  customer_name?: string;
  contact_no?: string;
  address?: string;
  service_type?: string;
  status?: string;
  deal_value?: number;
  remarks?: string;
  created_at?: string;
  social_media_link?: string;
}

interface Props {
  ticket: CrmTicket;
  onClick?: (ticket: CrmTicket) => void;
}

const STATUS_COLORS: { [status: string]: string } = {
  Lead: '#42A5F5',
  Warm: '#FFA726',
  Engage: '#AB47BC',
  Prospect: '#26A69A',
  'Close Deal': '#66BB6A',
  Lost: '#EF5350'
};

const DAY_IN_MS = 24 * 60 * 60 * 1000;

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  card: {
    cursor: 'pointer',
    borderLeft: '5px solid transparent',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[4]
    }
  },
  content: {
    padding: theme.spacing(1.5),
    '&:last-child': {
      paddingBottom: theme.spacing(1.5)
    }
  },
  customerName: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: theme.spacing(0.5)
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: theme.spacing(0.75)
  },
  icon: {
    fontSize: 14,
    color: theme.palette.text.secondary,
    marginTop: 2,
    flexShrink: 0
  },
  rowText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    wordBreak: 'break-word'
  },
  link: {
    fontSize: 12,
    color: theme.palette.primary.main,
    wordBreak: 'break-all',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  chipRow: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  dealValue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#66BB6A',
    marginLeft: 'auto'
  },
  remarks: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    marginTop: theme.spacing(0.75)
  }
}));

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatPeso = (value?: number) => {
  if (value === undefined || value === null) return null;
  return `₱${Number(value).toLocaleString('en-PH')}`;
};

const getTicketAgeLabel = (value?: string) => {
  if (!value) return null;

  const date = new Date(value);
  if (isNaN(date.getTime())) return null;

  const days = Math.max(
    0,
    Math.floor((Date.now() - date.getTime()) / DAY_IN_MS)
  );

  if (days <= 3) {
    return '';
  }

  return `${days} days`;
};

// ─── Component ───────────────────────────────────────────────────────────────

const Component: FC<Props> = ({ ticket, onClick }) => {
  const classes = useStyles();
  const statusColor = ticket.status ? STATUS_COLORS[ticket.status] : undefined;
  const ticketAgeLabel =
    ticket.status !== 'Close Deal'
      ? getTicketAgeLabel(ticket.created_at)
      : null;
  const dealValueLabel = formatPeso(ticket.deal_value);

  return (
    <Card
      className={classes.card}
      style={{ borderLeftColor: statusColor }}
      onClick={() => onClick?.(ticket)}
      elevation={1}
    >
      <CardContent className={classes.content}>
        {/* Name */}
        <Typography className={classes.customerName}>
          {ticket.customer_name || '---'}
        </Typography>

        {/* Type + Status + Deal Value chips */}
        <Box className={classes.chipRow}>
          {ticket.service_type && (
            <Chip
              label={ticket.service_type || '---'}
              size="small"
              variant="outlined"
            />
          )}
          {ticket.status && (
            <Chip label={ticket.status} size="small" color="primary" />
          )}
          {dealValueLabel && (
            <Typography className={classes.dealValue}>
              {dealValueLabel}
            </Typography>
          )}
        </Box>

        <Divider className={classes.divider} />

        {/* Contact */}
        {ticket.contact_no && (
          <Box className={classes.row}>
            <PhoneIcon className={classes.icon} />
            <Typography className={classes.rowText}>
              {ticket.contact_no}
            </Typography>
          </Box>
        )}

        {/* Address */}
        {ticket.address && (
          <Box className={classes.row}>
            <RoomIcon className={classes.icon} />
            <Typography className={classes.rowText}>
              {ticket.address}
            </Typography>
          </Box>
        )}

        {/* Social media link */}
        {ticket.social_media_link && (
          <Box className={classes.row}>
            <LinkIcon className={classes.icon} />
            <a
              href={ticket.social_media_link}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
              onClick={(e) => e.stopPropagation()}
            >
              {ticket.social_media_link}
            </a>
          </Box>
        )}

        {/* Remarks */}
        {ticket.remarks && (
          <Typography className={classes.remarks}>{ticket.remarks}</Typography>
        )}

        {/* Created at */}
        {ticket.created_at && (
          <Box
            className={classes.row}
            style={{ marginTop: 8, justifyContent: 'space-between' }}
          >
            <Box className={classes.row}>
              <ScheduleIcon className={classes.icon} />
              <Typography className={classes.rowText}>
                {formatDate(ticket.created_at)}
              </Typography>
            </Box>

            {ticketAgeLabel && (
              <Tooltip title={'total rotting days'}>
                <Chip
                  label={ticketAgeLabel}
                  size="small"
                  variant="outlined"
                  style={{
                    border: 'none',
                    background: colors.red[500],
                    color: 'white',
                    fontSize: '0.6em'
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const CrmTicketCard = memo(Component);
